import React from 'react';
import {
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  useTheme,
  Stack,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CircleFlag } from 'react-circle-flags';

import { DashboardTotalsByCountry } from '../types';

type TotalsCardMultiCountryProps = {
  title: string;
  data: DashboardTotalsByCountry[];
  loading: boolean;
};

const SmallTableCell = styled(TableCell)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
}));

const formatMoney = (value: number) => {
  const n = value ?? 0;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  return `$ ${formatted}`;
};

const formatInt = (value: number) => new Intl.NumberFormat('pt-BR').format(value ?? 0);

const getValueColor = (theme: any, value: number) =>
  value > 0 ? theme.palette.success.main : theme.palette.error.main;

const TotalsCard = ({ title, data, loading }: TotalsCardMultiCountryProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 2, background: theme.palette.background.paper }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.primary">
          {t(title)}
        </Typography>

        <Divider sx={{ mt: 1, mb: 1.5 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SmallTableCell sx={{ fontWeight: 600 }}>
                  {t('dashboard.conciliations-state.country', 'Country')}
                </SmallTableCell>

                <SmallTableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('dashboard.totals.fv_amount', 'Fv Amount')}
                </SmallTableCell>

                <SmallTableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('dashboard.totals.fs_amount', 'Fs Amount')}
                </SmallTableCell>

                <SmallTableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('dashboard.totals.fv_gcs', 'Fv GCs')}
                </SmallTableCell>

                <SmallTableCell align="center" sx={{ fontWeight: 600 }}>
                  {t('dashboard.totals.fs_gcs', 'Fs GCs')}
                </SmallTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading &&
                ['skeleton-1', 'skeleton-2', 'skeleton-3'].map(key => (
                  <TableRow key={key}>
                    <SmallTableCell>
                      <Skeleton width={160} />
                    </SmallTableCell>
                    <SmallTableCell align="center">
                      <Skeleton width={90} />
                    </SmallTableCell>
                    <SmallTableCell align="center">
                      <Skeleton width={90} />
                    </SmallTableCell>
                    <SmallTableCell align="center">
                      <Skeleton width={60} />
                    </SmallTableCell>
                    <SmallTableCell align="center">
                      <Skeleton width={60} />
                    </SmallTableCell>
                  </TableRow>
                ))}

              {!loading &&
                (data?.length ? data : []).map(row => {
                  const flexAmount = row.flex?.amount ?? 0;
                  const sovosAmount = row.sovos?.amount ?? 0;
                  const flexGcs = row.flex?.gcs ?? 0;
                  const sovosGcs = row.sovos?.gcs ?? 0;

                  return (
                    <TableRow
                      key={row.country}
                      hover
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        '&&:hover': { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      <SmallTableCell component="th" scope="row">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <CircleFlag countryCode={(row.country || '').toLowerCase()} height="20" />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {t(`operativeStructure.countries.${row.country}`)}
                            </Typography>
                          </Box>
                        </Stack>
                      </SmallTableCell>

                      {/* FLEX Amount */}
                      <SmallTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          sx={{ whiteSpace: 'nowrap' }}
                          variant="body2"
                          fontWeight={700}
                          color={getValueColor(theme, flexAmount)}
                        >
                          {formatMoney(flexAmount)}
                        </Typography>
                      </SmallTableCell>

                      {/* SOVOS Amount */}
                      <SmallTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          sx={{ whiteSpace: 'nowrap' }}
                          variant="body2"
                          fontWeight={700}
                          color={getValueColor(theme, sovosAmount)}
                        >
                          {formatMoney(sovosAmount)}
                        </Typography>
                      </SmallTableCell>

                      {/* FLEX GCs */}
                      <SmallTableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={getValueColor(theme, flexGcs)}
                        >
                          {formatInt(flexGcs)}
                        </Typography>
                      </SmallTableCell>

                      {/* SOVOS GCs */}
                      <SmallTableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={getValueColor(theme, sovosGcs)}
                        >
                          {formatInt(sovosGcs)}
                        </Typography>
                      </SmallTableCell>
                    </TableRow>
                  );
                })}

              {!loading && (!data || data.length === 0) && (
                <TableRow>
                  <SmallTableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.noData', 'No data')}
                    </Typography>
                  </SmallTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default TotalsCard;
